import { defineComponent, h, nextTick } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import { QBtn, QCard, QCardSection, QDialog, QSpace, QToolbar, QToolbarTitle } from 'quasar';
import { buildHtmlEditorExtensions } from './richText/tiptap/buildHtmlEditorExtensions.js';
import './schemaform/formHtmlEditor.css';

/** TipTap dialog for editing rich HTML parameter descriptions (ApiDocumentation). */
export default defineComponent({
    name: 'api-tester-param-description-dialog',
    components: {
        EditorContent,
        QBtn,
        QCard,
        QCardSection,
        QDialog,
        QSpace,
        QToolbar,
        QToolbarTitle,
    },
    props: {
        modelValue: { type: Boolean, default: false },
        paramName: { type: String, default: '' },
        initialHtml: { type: String, default: '' },
    },
    emits: ['update:modelValue', 'save'],
    data() {
        return {
            editor: null,
        };
    },
    watch: {
        modelValue(open) {
            if (open) {
                nextTick(() => this.mountEditor());
            } else {
                this.teardownEditor();
            }
        },
        initialHtml() {
            if (this.modelValue && this.editor) {
                const next = (this.initialHtml ?? '').toString();
                this.editor.commands.setContent(next.trim() ? next : '<p></p>', {
                    contentType: 'html',
                    emitUpdate: false,
                });
            }
        },
    },
    methods: {
        mountEditor() {
            this.teardownEditor();
            const initial = (this.initialHtml ?? '').toString();
            this.editor = new Editor({
                extensions: buildHtmlEditorExtensions({ allowBase64Images: false }),
                content: initial.trim() ? initial : '<p></p>',
                contentType: 'html',
                editable: true,
                editorProps: {
                    attributes: {
                        class: 'form-html-editor-prose tiptap',
                    },
                },
            });
            nextTick(() => this.editor?.commands?.focus?.('end'));
        },
        teardownEditor() {
            if (this.editor) {
                this.editor.destroy();
                this.editor = null;
            }
        },
        emitOpen(v) {
            this.$emit('update:modelValue', v);
        },
        onSave() {
            const html = this.editor?.getHTML?.() ?? '';
            this.$emit('save', html);
            this.emitOpen(false);
        },
        onCancel() {
            this.emitOpen(false);
        },
    },
    beforeUnmount() {
        this.teardownEditor();
    },
    render() {
        const shellStyle = {
            width: 'min(92vw, 900px)',
            maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        };
        const bodyStyle = {
            flex: '1 1 auto',
            minHeight: '280px',
            maxHeight: 'min(70vh, 640px)',
            overflow: 'auto',
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '8px',
            padding: '8px',
        };
        return (
            <QDialog
                modelValue={this.modelValue}
                onUpdate:modelValue={(v) => this.emitOpen(v)}
            >
                <QCard flat bordered style={shellStyle} class="api-tester-param-desc-dialog">
                    <QToolbar class="bg-primary text-white" style={{ flexShrink: 0 }}>
                        <QToolbarTitle class="text-white">
                            Parameter description{this.paramName ? `: ${this.paramName}` : ''}
                        </QToolbarTitle>
                        <QSpace />
                        <QBtn
                            flat
                            round
                            dense
                            color="white"
                            icon="mdi-close"
                            onClick={() => this.onCancel()}
                        />
                    </QToolbar>
                    <QCardSection class="q-pt-sm q-pb-md" style={{ flex: '1 1 auto', minHeight: 0 }}>
                        <div style={bodyStyle}>{this.editor ? <EditorContent editor={this.editor} /> : null}</div>
                        <div class="row justify-end q-gutter-sm q-mt-md">
                            <QBtn flat color="primary" label="Cancel" onClick={() => this.onCancel()} />
                            <QBtn unelevated color="primary" label="Apply" onClick={() => this.onSave()} />
                        </div>
                    </QCardSection>
                </QCard>
            </QDialog>
        );
    },
});
